import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import Divider from "components/divider";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Toggle from "components/form/toggle/Toggle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/";
import { GetAccountingReference, IAccount } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import "./facilitySettings.scss";

interface IProps {
  state: Partial<IPutTeeSheetSettings>;
  updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
}

const MINIMUM_DAY_INPUT = 1;
const MAXIMUM_DAY_INPUT = 31;

export default function FacilityAccountDefaultsSettings(props: IProps) {
  const { state, updateState } = props;
  const { t } = useTranslation();
  const { Option } = Select;
  const dispatch = useAppDispatch();
  const [accountingReferenceTypes, setAccountingReferenceTypes] = useState<IAccount[]>([]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccountingReferences(source.token);
    return () => source.cancel();
  }, []);

  async function loadAccountingReferences(cancelToken: CancelToken) {
    const res = await GetAccountingReference(
      {
        group: "accounts_receivable",
      },
      true,
      cancelToken,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading accounts")));
      return;
    }

    setAccountingReferenceTypes(
      res?.data?.map((account: any) => ({
        id: account.id,
        title: account.title,
        currency: account.currency,
        global: account.global,
        account_number: account.account_number,
        limit: account.limit,
        limit_is_valid: account.limit_is_valid ?? false,
        accounting_reference_id: account.accounting_reference_id,
      })),
    );
  }

  return (
    <div className="facility-settings-section p-2">
      <div className="facility-settings-toggle-stack">
        <Toggle
          id="accounts_auto_pay_balance"
          checked={state?.accounts_auto_pay_balance}
          onChange={e => updateState("accounts_auto_pay_balance", e.target.checked)}
          labelRight={t("Auto pay balance")}
          labelSupport={t("Automatically pay remaining balance monthly to keep accounts up to date.")}
        />
      </div>

      {state?.accounts_auto_pay_balance ? (
        <FormLayout>
          <FormLayout.Group>
            <Input
              label={t("Payment day")}
              type="number"
              id="accounts_auto_pay_anchor_date"
              value={
                state?.accounts_auto_pay_anchor_date < MINIMUM_DAY_INPUT
                  ? ""
                  : state?.accounts_auto_pay_anchor_date > MAXIMUM_DAY_INPUT
                  ? MAXIMUM_DAY_INPUT
                  : state?.accounts_auto_pay_anchor_date
              }
              onChange={e => updateState("accounts_auto_pay_anchor_date", Number(e.target?.value))}
              suffix={t("Day of month")}
              error={state?.accounts_auto_pay_anchor_date === 0}
            />
          </FormLayout.Group>
        </FormLayout>
      ) : null}

      <Divider />

      <div className="facility-settings-toggle-stack">
        <Toggle
          id="accounts_send_statement"
          checked={state?.accounts_send_statement}
          onChange={e => updateState("accounts_send_statement", e.target.checked)}
          labelRight={t("Send statements")}
          labelSupport={t("Email statements to the customer and save in house account to help maintain a record.")}
        />
      </div>

      <Divider />

      <FormLayout>
        <FormLayout.Group>
          <Select
            label={t("Default accounting reference")}
            defaultValue={state?.accounts_accounting_reference_id}
            onChange={(value: number) => updateState("accounts_accounting_reference_id", value)}
          >
            {accountingReferenceTypes?.map((account, accountIndex) => {
              return (
                <Option key={accountIndex} value={account.id}>
                  {account.title}
                </Option>
              );
            })}
          </Select>
        </FormLayout.Group>
      </FormLayout>
    </div>
  );
}
